/**
 * LE PARCOURS — contrats et règles pures (lot P0, AB#972, épopée AB#961 ;
 * `docs/pilotage/passe-ux-prod/RAPPORT-PASSE-UX-PROD.md` § 6).
 *
 * Un parcours est une suite ORDONNÉE d'étapes (capsules, fiches, exercices)
 * d'UNE matière, rangée en blocs (« Prérequis », « Vidéos », « Exercices »…),
 * avec un nom, une date butoir facultative et un état. En base, c'est une
 * `SequenceTravail` élargie (migration `20261019120000_parcours`) : la « liste
 * du tuteur » et le parcours sont une seule mécanique.
 *
 * ─── Les décisions du fondateur (19 oct. 2026, AB#961) ─────────────────────
 *
 *  - 20 étapes AU PLUS — un plafond, pas une cible : la borne de la liste du
 *    tuteur (`MAX_ELEMENTS_SEQUENCE`), une seule source ;
 *  - un parcours déposé par le tuteur : l'élève peut seulement le METTRE EN
 *    PAUSE, ni le modifier ni le supprimer ;
 *  - l'élève peut REFAIRE un parcours terminé, comme il refait un exercice :
 *    une NOUVELLE séquence, copiée, sans rien de fait ; l'ancienne reste dans
 *    l'historique ;
 *  - le MÊME numéro d'étape partout — élève, parent, tuteur : c'est le rang de
 *    l'élément dans la séquence, global, jamais un rang dans le bloc ;
 *  - le parent le voit en lecture, dans la fiche de l'enfant ;
 *  - pas de rappel envoyé par le tuteur.
 *
 * ─── Le moteur (`auto`) ────────────────────────────────────────────────────
 *
 * Pour les droits, un parcours du moteur se traite comme un parcours de
 * l'élève : aucun adulte ne l'a déposé, et l'ébauche est faite pour être
 * retouchée (§ 6 : « ébauche proposée, éditable »). La restriction du
 * fondateur protège ce qu'un TUTEUR a composé ; elle ne s'étend pas au moteur.
 *
 * Les fonctions de ce fichier sont PURES : le serveur s'en sert pour décider,
 * les écrans pour afficher le même « 3 sur 7 ».
 */
import { z } from 'zod';
import { estDateCivile, GENRES_DE_GESTE } from './manuel-objectif.js';
import { MAX_CARACTERES_MOT_SEQUENCE, MAX_CARACTERES_RAISON_ELEMENT, MAX_CARACTERES_TITRE_SEQUENCE, MAX_ELEMENTS_SEQUENCE, } from './manuel-tuteur.js';
import { MATIERES } from './roles.js';
/* -------------------------------------------------------------------------- */
/* 1. Les registres                                                           */
/* -------------------------------------------------------------------------- */
/** Qui a écrit un parcours — l'enum `AuteurParcours` du schéma. */
export const AUTEURS_PARCOURS = ['eleve', 'tuteur', 'auto'];
/** La nature d'un bloc — l'enum `NatureBloc` du schéma. Elle donne son titre par défaut. */
export const NATURES_BLOC = [
    'prerequis',
    'capsules',
    'fiches',
    'exercices',
    'revision',
    'libre',
];
/** Le titre d'un bloc qui n'en a pas reçu. Libellés courts. */
export const LIBELLES_NATURE_BLOC = {
    prerequis: 'Prérequis',
    capsules: 'Vidéos',
    fiches: 'Fiches',
    exercices: 'Exercices',
    revision: 'Révision',
    libre: 'Étapes',
};
/** Où en est un parcours — CALCULÉ (`etatDuParcours`), jamais stocké. */
export const ETATS_PARCOURS = ['en_cours', 'en_pause', 'termine', 'archive'];
/** Le plafond d'étapes : celui de la liste du tuteur, une seule source. */
export const MAX_ETAPES_PARCOURS = MAX_ELEMENTS_SEQUENCE;
export const MAX_CARACTERES_NOM_PARCOURS = MAX_CARACTERES_TITRE_SEQUENCE;
export const MAX_CARACTERES_TITRE_BLOC = 40;
/** Le plus long code de ressource accepté — les codes du dépôt en font moins de 30. */
export const MAX_CARACTERES_CODE_RESSOURCE = 120;
/** La plus longue ancre de fiche acceptée (`piege-signe-oublie`) — celles du dépôt en font moins de 40. */
export const MAX_CARACTERES_ANCRE_ETAPE = 80;
/** Un parcours refusé à la création ou à la modification : 422, ce code. */
export const CODE_PARCOURS_INVALIDE = 'PARCOURS_INVALIDE';
/** Le geste n'est pas permis à ce compte sur ce parcours (`peutModifier`…) : 403. */
export const CODE_PARCOURS_INTERDIT = 'PARCOURS_INTERDIT';
/* -------------------------------------------------------------------------- */
/* 2. Ce que l'élève envoie                                                   */
/* -------------------------------------------------------------------------- */
const texte = (max) => z.string().trim().min(1).max(max);
export const etapeParcoursBodySchema = z.object({
    genre: z.enum(GENRES_DE_GESTE),
    /** `MAT-3-EXP-N2-1-03`, `MAT-3-EXP-F2`, `MAT-3-EXP-C03`. */
    code: texte(MAX_CARACTERES_CODE_RESSOURCE),
    raison: texte(MAX_CARACTERES_RAISON_ELEMENT).optional(),
    /**
     * Pour une fiche : la section où l'ouvrir — le piège que l'ébauche propose
     * quand une erreur revient (P4, AB#976). Absente : la fiche entière.
     */
    ancre: texte(MAX_CARACTERES_ANCRE_ETAPE).optional(),
});
export const blocParcoursBodySchema = z.object({
    nature: z.enum(NATURES_BLOC),
    /** Absent : le titre de sa nature (`LIBELLES_NATURE_BLOC`). */
    titre: texte(MAX_CARACTERES_TITRE_BLOC).optional(),
    /** Un bloc n'est jamais vide. */
    etapes: z.array(etapeParcoursBodySchema).min(1),
});
/** Une date civile RÉELLE, `AAAA-MM-JJ`. */
const dateCivile = z.string().refine(estDateCivile, { message: 'La date n’est pas valide.' });
/**
 * Les règles d'une liste de blocs : de 1 à `MAX_ETAPES_PARCOURS` étapes au
 * total, et aucune ressource deux fois (la règle du dépôt du tuteur).
 */
function verifierBlocs(blocs, ctx) {
    const total = blocs.reduce((n, b) => n + b.etapes.length, 0);
    if (total > MAX_ETAPES_PARCOURS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['blocs'],
            message: `Un parcours compte ${MAX_ETAPES_PARCOURS} étapes au plus.`,
        });
    }
    const vues = new Set();
    for (const b of blocs) {
        for (const e of b.etapes) {
            const cle = `${e.genre}:${e.code}`;
            if (vues.has(cle)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['blocs'],
                    message: 'Une même ressource est en double.',
                });
                return;
            }
            vues.add(cle);
        }
    }
}
const blocsSchema = z.array(blocParcoursBodySchema).min(1).superRefine(verifierBlocs);
/** POST — l'élève crée un parcours. Au moins un bloc, donc au moins une étape. */
export const creerParcoursBodySchema = z.object({
    nom: texte(MAX_CARACTERES_NOM_PARCOURS),
    matiere: z.enum(MATIERES),
    /** Date butoir facultative. */
    pourLe: dateCivile.optional(),
    blocs: blocsSchema,
});
/**
 * PATCH — modifier un parcours. Chaque champ absent est laissé tel quel ;
 * `pourLe` à `null` l'efface. `blocs`, s'il est donné, REMPLACE la liste dans
 * l'ordre donné ; les étapes déjà faites gardent leur « fait » (c'est le
 * serveur qui le reporte). La matière ne change pas : un autre parcours.
 */
export const modifierParcoursBodySchema = z
    .object({
    nom: texte(MAX_CARACTERES_NOM_PARCOURS).optional(),
    pourLe: dateCivile.nullable().optional(),
    blocs: blocsSchema.optional(),
})
    .refine((m) => m.nom !== undefined || m.pourLe !== undefined || m.blocs !== undefined, {
    message: 'Rien à modifier.',
});
/** Le mot du tuteur sur un parcours qu'il dépose — même borne que la séquence. */
export const MAX_CARACTERES_MOT_PARCOURS = MAX_CARACTERES_MOT_SEQUENCE;
/** L'ébauche (P4) : de 1 à 5 chapitres, de 10 minutes à 5 heures. */
export const MAX_CHAPITRES_EBAUCHE = 5;
export const MIN_MINUTES_EBAUCHE = 10;
export const MAX_MINUTES_EBAUCHE = 300;
/**
 * POST `/parcours/ebauche` (élève) et `/eleves/:eleveId/parcours/ebauche`
 * (tuteur) — les trois questions : quels chapitres, combien de temps, pour
 * quand. Rien n'est écrit : la réponse est une proposition
 * (`EbaucheParcoursResponse`), que l'écran de création reprend.
 */
export const ebaucheParcoursBodySchema = z.object({
    matiere: z.enum(MATIERES),
    /** Des codes de chapitre, dans l'ordre choisi ; aucun deux fois. */
    chapitres: z
        .array(texte(MAX_CARACTERES_CODE_RESSOURCE))
        .min(1)
        .max(MAX_CHAPITRES_EBAUCHE)
        .refine((c) => new Set(c).size === c.length, { message: 'Un chapitre est en double.' }),
    minutes: z.number().int().min(MIN_MINUTES_EBAUCHE).max(MAX_MINUTES_EBAUCHE),
    pourLe: dateCivile.optional(),
});
/** Un parcours terminé reste lu par le parent pendant ce nombre de jours. */
export const JOURS_PARCOURS_TERMINE_CHEZ_LE_PARENT = 30;
/**
 * POST `/v1/tuteur/manuel/eleves/:eleveId/parcours` et
 * `…/cohortes/:cohorteId/parcours` : la création de l'élève, plus le mot du
 * tuteur (servi à l'élève).
 */
export const deposerParcoursTuteurBodySchema = creerParcoursBodySchema.extend({
    mot: texte(MAX_CARACTERES_MOT_PARCOURS).optional(),
});
/**
 * « 3 sur 7 » — trois étapes FAITES sur sept, quel que soit leur ordre ; la
 * prochaine est la PREMIÈRE non faite, dans l'ordre des rangs (on reprend là
 * où le parcours en est, pas après la dernière faite). C'est le numéro affiché
 * chez l'élève, le parent et le tuteur. Le numéro d'une étape est sa place
 * dans l'ordre des rangs (1, 2, 3…) : des rangs écrits sans trou le rendent
 * égal au rang.
 */
export function progressionDuParcours(etapes) {
    const ordre = [...etapes].sort((a, b) => a.rang - b.rang);
    const i = ordre.findIndex((e) => e.faitLe === null);
    return {
        faites: ordre.filter((e) => e.faitLe !== null).length,
        total: ordre.length,
        prochaine: i < 0 ? null : i + 1,
    };
}
/** Retiré l'emporte sur terminé, terminé sur en pause. */
export function etatDuParcours(p) {
    if (p.archiveLe !== null)
        return 'archive';
    if (p.termineLe !== null)
        return 'termine';
    if (p.pauseLe !== null)
        return 'en_pause';
    return 'en_cours';
}
/** Le parcours appartient-il à cet acteur — l'a-t-il écrit ? */
function estLeSien(p, a) {
    if (a.role === 'eleve')
        return p.auteurRole === 'eleve' || p.auteurRole === 'auto';
    if (a.role === 'tuteur')
        return p.auteurRole === 'tuteur' && p.tuteurId === a.id;
    return false;
}
/**
 * Modifier le nom, la date butoir, les étapes : le sien seulement, et tant
 * qu'il n'est ni terminé ni retiré (un parcours terminé se REFAIT). Une pause
 * n'empêche pas de le retoucher. L'élève ne modifie jamais celui du tuteur.
 */
export function peutModifier(p, a) {
    const etat = etatDuParcours(p);
    return (etat === 'en_cours' || etat === 'en_pause') && estLeSien(p, a);
}
/**
 * Mettre en pause : l'élève, sur TOUT parcours en cours de sa liste — le sien
 * comme celui du tuteur (le seul geste qu'il a sur celui-là) ; le tuteur, sur
 * les siens.
 */
export function peutMettreEnPause(p, a) {
    if (etatDuParcours(p) !== 'en_cours')
        return false;
    return a.role === 'eleve' || estLeSien(p, a);
}
/** Reprendre un parcours en pause : les mêmes que pour la pause. */
export function peutReprendre(p, a) {
    if (etatDuParcours(p) !== 'en_pause')
        return false;
    return a.role === 'eleve' || estLeSien(p, a);
}
/**
 * Supprimer — c'est-à-dire RETIRER (`archiveLe`) : rien n'est effacé, les
 * travaux faits restent au dossier. Le sien seulement, terminé compris.
 */
export function peutSupprimer(p, a) {
    return etatDuParcours(p) !== 'archive' && estLeSien(p, a);
}
/** Refaire : l'élève, sur tout parcours TERMINÉ de sa liste, quel qu'en soit l'auteur. */
export function peutRefaire(p, a) {
    return a.role === 'eleve' && etatDuParcours(p) === 'termine';
}
/**
 * Les étapes rangées sous leurs blocs, dans l'ordre des blocs puis des rangs.
 * Les étapes sans bloc (toute séquence d'avant P0) forment le BLOC IMPLICITE,
 * en tête : `bloc` y vaut `null`. Une étape dont le bloc n'existe plus y va
 * aussi. Un bloc sans étape n'est pas rendu.
 */
export function rangerParBlocs(blocs, etapes) {
    const ordre = [...etapes].sort((a, b) => a.rang - b.rang);
    const connus = new Set(blocs.map((b) => b.id));
    const implicite = ordre.filter((e) => e.blocId === null || !connus.has(e.blocId));
    const ranges = [...blocs]
        .sort((a, b) => a.rang - b.rang)
        .map((bloc) => ({ bloc, etapes: ordre.filter((e) => e.blocId === bloc.id) }));
    return [
        ...(implicite.length > 0 ? [{ bloc: null, etapes: implicite }] : []),
        ...ranges.filter((r) => r.etapes.length > 0),
    ];
}
/** Le titre d'un bloc servi. */
export const titreDuBloc = (b) => b?.titre ?? LIBELLES_NATURE_BLOC[b?.nature ?? 'libre'];
/**
 * REFAIRE un parcours : une NOUVELLE séquence, mêmes blocs et mêmes étapes
 * dans le même ordre, AUCUNE étape faite, `origineId` posé ; l'ancienne reste
 * telle quelle dans l'historique. Le bloc implicite d'une séquence d'avant P0
 * devient un bloc `libre` sans titre.
 *
 * La copie est à l'ÉLÈVE, même refaite d'un parcours du tuteur : c'est lui qui
 * la lance, le tuteur ne l'a pas déposée (elle n'entre donc pas dans SA liste),
 * et l'auteur d'origine reste lisible par `origineId`. Le mot du tuteur suit
 * l'étape : il dit encore par quoi commencer.
 */
export function copiePourRefaire(p) {
    return {
        eleveId: p.eleveId,
        auteurRole: 'eleve',
        tuteurId: null,
        cohorteId: null,
        origineId: p.id,
        titre: p.titre,
        matiere: p.matiere,
        note: p.note,
        pourLe: null,
        blocs: rangerParBlocs(p.blocs, p.elements).map(({ bloc, etapes }) => ({
            nature: bloc?.nature ?? 'libre',
            titre: bloc?.titre ?? null,
            elements: etapes.map((e) => ({
                genre: e.genre,
                code: e.code,
                raison: e.raison,
                /* Le piège d'une fiche reste son piège : la copie l'ouvre au même endroit. */
                ancre: e.ancre ?? null,
                faitLe: null,
                ficheTravailId: null,
            })),
        })),
    };
}
//# sourceMappingURL=parcours.js.map